import { AntButton } from '@/common/ant-components/AntButton';
import GlobalDivider from '@/utils/divider/GlobalDivider';
import { valueLimiterPercentage } from '@/utils/validation/FormValidation';
import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { useLocation } from 'react-router-dom';

const { Option } = Select;

/**
 * Component for displaying a modal to add sales description.
 * @param {Object} props - Component props
 * @param {boolean} props.isModalVisible - Whether the modal is visible
 * @param {Function} props.setIsModalVisible - Function to set modal visibility
 * @param {Object} props.formData - Object containing sales description data
 * @param {Function} props.onHandleSalesDescription - Function to handle sales description changes
 * @returns {JSX.Element} Sales Description Modal component
 */
const SalesDescriptionModal = ({
  isModalVisible,
  setIsModalVisible,
  onHandleSalesDescription,
  formData,
  // form,
  setFormData,
}) => {
  const [dragDisabled, setDragDisabled] = useState(true);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 });
  const [form] = Form.useForm();
  const location = useLocation();

  const isPurchase = ['/codewindow/', '/transaction/purchase/edit/']?.some(
    (path) => location.pathname.includes(path),
  );

  /**
   * Handles mouse over event to enable dragging.
   */
  const handleMouseOver = () => {
    if (dragDisabled) {
      setDragDisabled(false);
    }
  };

  /**
   * Handles mouse out event to disable dragging.
   */
  const handleMouseOut = () => {
    setDragDisabled(true);
  };

  /**
   * Handles stopping drag and updates modal offset.
   * @param {Event} e - Draggable event
   * @param {Object} data - Draggable data object containing x and y offsets
   */
  const handleDragStop = (e, data) => {
    setModalOffset({ x: data.x, y: data.y });
  };

  useEffect(() => {
    form.setFieldsValue(formData);
  }, [formData]);

  return (
    <Modal
      width={800}
      title={
        <div
          style={{
            cursor: 'move',
            marginBottom: '10px',
          }}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <GlobalDivider name="Add Sales Description" orientation="center" />
        </div>
      }
      centered
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      modalRender={(modal) => (
        <Draggable
          disabled={dragDisabled}
          onStop={handleDragStop}
          position={modalOffset}
        >
          <div>{modal}</div>
        </Draggable>
      )}
      footer={[
        <AntButton
          onClick={async () => {
            try {
              await form.validateFields();

              setIsModalVisible(false);
            } catch (error) {
              // console.error(error);
            }
          }}
        >
          Submit
        </AntButton>,
        <AntButton
          color="red"
          key="backs"
          onClick={() => {
            setIsModalVisible(false);
          }}
        >
          Cancel
        </AntButton>,
      ]}
    >
      {isModalVisible && (
        <div>
          <Form layout="vertical" form={form}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Making Charge"
                  name="sMakingChargeType"
                >
                  <Select
                    placeholder="Select charge type"
                    onChange={(value) => {
                      onHandleSalesDescription('sMakingChargeType', value);
                      setFormData({
                        ...formData,
                        ...form.getFieldsValue(),
                        sMakingCharge: 0,
                        sMakingAmount: 0,
                      });
                      form.setFieldsValue({
                        ...formData,
                        ...form.getFieldsValue(),
                        sMakingCharge: 0,
                        sMakingAmount: 0,
                      });
                    }}
                  >
                    <Option value="amount">Amount</Option>
                    <Option value="percentage">Percentage</Option>
                    <Option value="rate">Rate</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Charge"
                  name="sMakingCharge"
                >
                  <Input
                    type="text"
                    placeholder="Enter charge"
                    onChange={(e) =>
                      onHandleSalesDescription('sMakingCharge', e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (
                        form.getFieldValue('sMakingChargeType') === 'percentage'
                      ) {
                        valueLimiterPercentage(e, 100);
                      }
                    }}
                    disabled={formData?.sMakingChargeType === 'amount'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Amount"
                  name="sMakingAmount"
                >
                  <Input
                    value={formData.sMakingAmount}
                    type="text"
                    placeholder="Enter amount"
                    onChange={(e) =>
                      onHandleSalesDescription('sMakingAmount', e.target.value)
                    }
                    disabled={
                      formData?.sMakingChargeType === 'percentage' ||
                      formData?.sMakingChargeType === 'rate'
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Wastage Type"
                  name="sWastageType"
                >
                  <Select
                    placeholder="Select wastage type"
                    onChange={(value) => {
                      onHandleSalesDescription('sWastageType', value);
                      setFormData({
                        ...formData,
                        ...form.getFieldsValue(),
                        sWastageCharge: 0,
                        sWastageWeight: 0,
                      });
                      form.setFieldsValue({
                        ...formData,
                        ...form.getFieldsValue(),
                        sWastageCharge: 0,
                        sWastageWeight: 0,
                      });
                    }}
                  >
                    <Option value="w">W</Option>
                    <Option value="gw">GW</Option>
                    <Option value="nw">NW</Option>
                    <Option value="pw">PW</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Charge"
                  name="sWastageCharge"
                >
                  <Input
                    value={formData.sWastageCharge}
                    type="text"
                    placeholder="Enter charge"
                    onChange={(e) =>
                      onHandleSalesDescription('sWastageCharge', e.target.value)
                    }
                    disabled={formData?.sWastageType === 'w'}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Weight"
                  name="sWastageWeight"
                >
                  <Input
                    value={formData.sWastageWeight}
                    type="text"
                    placeholder="Enter weight"
                    onChange={(e) =>
                      onHandleSalesDescription('sWastageWeight', e.target.value)
                    }
                    disabled={formData?.sWastageType != 'w'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Extra Amount"
                  name="sExtraAmount"
                >
                  <Input
                    type="text"
                    placeholder="Enter extra amount"
                    onChange={(e) =>
                      onHandleSalesDescription('sExtraAmount', e.target.value)
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Fixed amount"
                  name="sFixedAmount"
                >
                  <Select
                    defaultValue="no"
                    disabled={isPurchase}
                    onChange={(value) => {
                      onHandleSalesDescription('sFixedAmount', value);
                      setFormData({
                        ...formData,
                        ...form.getFieldsValue(),
                        sNetAmount: 0,
                      });
                      form.setFieldsValue({
                        ...formData,
                        ...form.getFieldsValue(),
                        sNetAmount: 0,
                      });
                      form.validateFields();
                    }}
                  >
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  className="username"
                  label="Net amount"
                  name="sNetAmount"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (
                          (!value || value == 0) &&
                          form.getFieldValue('sFixedAmount') === 'yes'
                        ) {
                          return Promise.reject('Net amount is required');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="text"
                    disabled={form.getFieldValue('sFixedAmount') === 'no'}
                    onChange={(e) =>
                      onHandleSalesDescription('sNetAmount', e.target.value)
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default SalesDescriptionModal;
